import { action, computed, observable } from 'mobx';
import * as Validator from 'validatorjs';
import { bind } from './Bind';
import { humanizeString } from './HumanizeString';

export type FormFields<T> = { [K in keyof T]-?: IFormField<T[K]> };
export interface IFormField<T> {
    onMount(validation: string): void;
    onChange(value: T): void;
    validate(): void;
    addErrors(errors: string[]): void;
    setValue(value: T): void;
    clear(): void;
    onSubmit(): void;
    value: T;
    state: 'success' | 'warning' | 'danger' | undefined;
    errorState?: 'success' | 'warning' | 'danger';
    fields: FormFields<T>;
    touched: boolean;
    dirty: boolean;
    errors: string[];
    name: string;
    submitted: boolean;
}

export class PrimitiveFormField<T> implements IFormField<T> {
    public constructor(name: string, value: T) {
        this.initialValue = value;
        this.name = name;
        this.value = value;
    }

    @action
    public onMount(validation: string) {
        this.validation = validation;
        this.touched = false;
    }

    @action
    public onChange(value: T) {
        this.value = value;
        this.validate();
    }

    @action
    public validate() {
        this.touched = true;

        if (this.validation) {
            const data: any = {};
            const rules: any = {};
            const names: any = {};
            data[this.name] = this.value;
            rules[this.name] = this.validation;
            names[this.name] = this.isNumberName(this.name) ? '' : humanizeString(this.name);
            const validator = new Validator(data, rules);
            validator.setAttributeNames(names);
            this.errorState = validator.passes() ? undefined : 'danger';
            this.errors = validator.errors.get(this.name);
        } else if (this.errors.length) {
            this.errorState = undefined;
            this.errors = [];
        }
    }

    @action
    public addErrors(errors: string[]) {
        this.errorState = 'danger';
        this.errors = errors;
    }

    @action
    public setValue(value: T) {
        this.value = value;
    }

    @action
    public onSubmit() {
        this.submitted = true;
    }

    @action
    public clear() {
        this.value = this.initialValue;
        this.touched = false;
        this.errors.length = 0;
        this.errorState = undefined;
        this.submitted = false;
    }

    public get fields(): FormFields<T> {
        throw new Error('Unsupported operation');
    }

    @computed
    public get state() {
        return this.submitted ? this.errorState : undefined;
    }

    @computed
    public get dirty(): boolean {
        return this.touched ? this.value != this.initialValue : false;
    }

    @bind
    private isNumberName(name: string) {
        if (isNaN(parseInt(name))) {
            return false;
        } else {
            return true;
        }
    }

    @observable
    public value: T;
    @observable
    public errorState?: 'success' | 'warning' | 'danger';
    @observable
    public touched = false;
    @observable
    public errors: string[] = [];
    @observable
    public name: string;
    @observable
    public submitted = false;
    @observable
    private validation?: string;
    private initialValue: T;
}
